import { useOnboarding } from '@/hooks/useOnboarding';
import { useUsersStore } from '@/stores/users';
import { EKioskRouterNames, EUserRoles } from '@/types';
import { SESSION_STORAGE_KEYS } from '@/utils/constants';
import { isSafeRedirect } from '@/utils/safeRedirect';
import { useEffect } from 'react';
import { matchPath, Outlet, useNavigate } from 'react-router-dom';

const isNotAuthPaths = [
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__CONTINUE}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__LOGIN_USER}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__SIGNUP_USER}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__FORGOT_PASSWORD}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__RESET_PASSWORD}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__PASSWORD_RESET_CONFIRMATION}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__LOGOUT_NOTICE}`,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__UNAVAILABLE}`,
];
/**
 * Controls route access and navigation flow within the kiosk application.
 *
 * Handles three sequential redirect scenarios in priority order:
 * 1. Unauthenticated users (non-PATIENT role) are redirected to the login page.
 *    The current path is saved to sessionStorage so they can be returned after login.
 * 2. Authenticated users with incomplete onboarding are redirected to the
 *    appropriate onboarding step determined by {@link useOnboarding}.
 * 3. Once authenticated and onboarding is complete, if a saved return path exists
 *    in sessionStorage (set during scenario 1), the user is redirected there.
 *    This redirect only fires from the kiosk root to avoid a race condition with
 *    the onboarding form's own post-submit navigation to the kiosk root.
 * @returns JSX.Element - Renders child routes via Outlet, or null while a redirect is pending.
 */
export const KioskProtectedLayout = () => {
  const redirect = useOnboarding();
  const role = useUsersStore((state) => state.role);
  const navigate = useNavigate();
  // const { pathname, search } = useLocation();
  const { pathname, search } = window.location;

  const isNotAuthPath = isNotAuthPaths.some((path) =>
    matchPath(pathname, path),
  );
  const isAuthenticated = role === EUserRoles.PATIENT;
  const isKioskRoot = !!matchPath(EKioskRouterNames.KIOSK, pathname);
  const isOnRedirectPath = !!(redirect && matchPath(redirect, pathname));
  const shouldRedirectToAuth = !isKioskRoot && !isAuthenticated;
  const shouldRedirectToOnboarding =
    isAuthenticated && !!redirect && !isOnRedirectPath;
  // True only when the user is authenticated and all onboarding steps are complete.
  const isOnboardingComplete = isAuthenticated && !redirect;

  useEffect(() => {
    if (isNotAuthPath) return;
    if (shouldRedirectToAuth) {
      // User is not authenticated — save the intended destination (e.g. /kiosk/dashboard)
      // so they can be returned there after login, then redirect to the login page.
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.REDIRECT_PATH,
        pathname + search,
      );
      const isDashboard = !!matchPath(
        `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__DASHBOARD}`,
        pathname,
      );
      navigate(
        isDashboard
          ? `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__LOGIN_USER}`
          : `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__CONTINUE}`,
        { replace: true },
      );
    } else if (shouldRedirectToOnboarding) {
      // User is authenticated but onboarding is incomplete — send them to the
      // next required onboarding step.
      navigate(redirect, { replace: true });
    } else if (isOnboardingComplete && isKioskRoot) {
      // After onboarding completes the form navigates to the kiosk root, so we
      // wait until we are on the root before consuming the saved return path.
      // This prevents a race condition where the form's navigation would override
      // an early redirect triggered while still on the onboarding page.
      const returnPath = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.REDIRECT_PATH,
      );
      if (returnPath) {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_PATH);
        if (isSafeRedirect(returnPath)) {
          navigate(returnPath, { replace: true });
        }
      }
    }
  }, [
    shouldRedirectToAuth,
    shouldRedirectToOnboarding,
    isOnboardingComplete,
    isKioskRoot,
    navigate,
    redirect,
    pathname,
    search,
    isNotAuthPath,
  ]);

  // Render nothing while a redirect is in progress to avoid flashing protected content.
  if (shouldRedirectToAuth || shouldRedirectToOnboarding || isNotAuthPath) {
    return null;
  }

  return <Outlet />;
};
